import { pointFrom, pointRotateRads } from "@excalidraw/math";
import { useMemo } from "react";

import { isTextElement, isFrameLikeElement, reapplyAutoLayoutToAllFrames } from "@excalidraw/element";

import { getCommonBounds } from "@excalidraw/element";

import type { ElementsMap, ExcalidrawElement } from "@excalidraw/element/types";

import type { Scene } from "@excalidraw/element";

import { t } from "../../i18n";

import StatsDragInput from "./DragInput";
import { getAtomicUnits, isPropertyEditable } from "./utils";
import { getElementsInAtomicUnit, moveElement } from "./utils";

import type { DragInputCallbackType, DragFinishedCallbackType } from "./DragInput";
import type { AtomicUnit } from "./utils";
import type { AppState } from "../../types";

interface MultiPositionProps {
  property: "x" | "y";
  elements: readonly ExcalidrawElement[];
  elementsMap: ElementsMap;
  atomicUnits: AtomicUnit[];
  scene: Scene;
  appState: AppState;
}

const moveElements = (
  property: MultiPositionProps["property"],
  changeInTopX: number,
  changeInTopY: number,
  originalElements: readonly ExcalidrawElement[],
  originalElementsMap: ElementsMap,
  scene: Scene,
) => {
  for (let i = 0; i < originalElements.length; i++) {
    const origElement = originalElements[i];

    const [cx, cy] = [
      origElement.x + origElement.width / 2,
      origElement.y + origElement.height / 2,
    ];
    const [topLeftX, topLeftY] = pointRotateRads(
      pointFrom(origElement.x, origElement.y),
      pointFrom(cx, cy),
      origElement.angle,
    );

    const newTopLeftX =
      property === "x" ? Math.round(topLeftX + changeInTopX) : topLeftX;

    const newTopLeftY =
      property === "y" ? Math.round(topLeftY + changeInTopY) : topLeftY;

    moveElement(
      newTopLeftX,
      newTopLeftY,
      origElement,
      scene,
      originalElementsMap,
      false,
    );
  }
};

const moveGroupTo = (
  nextX: number,
  nextY: number,
  originalElements: ExcalidrawElement[],
  originalElementsMap: ElementsMap,
  scene: Scene,
) => {
  const elementsMap = scene.getNonDeletedElementsMap();
  const [x1, y1, ,] = getCommonBounds(originalElements);
  const offsetX = nextX - x1;
  const offsetY = nextY - y1;

  for (let i = 0; i < originalElements.length; i++) {
    const origElement = originalElements[i];

    const latestElement = elementsMap.get(origElement.id);
    if (!latestElement) {
      continue;
    }

    // bound texts are moved with their containers
    if (!isTextElement(latestElement) || !latestElement.containerId) {
      const [cx, cy] = [
        latestElement.x + latestElement.width / 2,
        latestElement.y + latestElement.height / 2,
      ];

      const [topLeftX, topLeftY] = pointRotateRads(
        pointFrom(latestElement.x, latestElement.y),
        pointFrom(cx, cy),
        latestElement.angle,
      );

      moveElement(
        topLeftX + offsetX,
        topLeftY + offsetY,
        origElement,
        scene,
        originalElementsMap,
        false,
      );
    }
  }
};

const handlePositionChange: DragInputCallbackType<
  MultiPositionProps["property"]
> = ({
  accumulatedChange,
  originalElements,
  originalElementsMap,
  shouldChangeByStepSize,
  nextValue,
  property,
  scene,
  originalAppState,
}) => {
  const elementsMap = scene.getNonDeletedElementsMap();

  if (nextValue !== undefined) {
    for (const atomicUnit of getAtomicUnits(
      originalElements,
      originalAppState,
    )) {
      const elementsInUnit = getElementsInAtomicUnit(
        atomicUnit,
        elementsMap,
        originalElementsMap,
      );

      if (elementsInUnit.length > 1) {
        const [x1, y1, ,] = getCommonBounds(
          elementsInUnit.map((el) => el.latest!),
        );
        let newTopLeftX = property === "x" ? nextValue : x1;
        let newTopLeftY = property === "y" ? nextValue : y1;

        // Convert frame-relative coordinates back to absolute coordinates for groups
        const firstElement = elementsInUnit[0]?.original;
        if (firstElement?.frameId) {
          const containingFrame = originalElementsMap.get(firstElement.frameId);
          if (containingFrame) {
            // Check if all elements in group are in the same frame
            const allInSameFrame = elementsInUnit.every(
              (el) => el.original.frameId === firstElement.frameId,
            );
            if (allInSameFrame) {
              if (property === "x") {
                newTopLeftX = nextValue + containingFrame.x;
              } else {
                newTopLeftY = nextValue + containingFrame.y;
              }
            }
          }
        }

        moveGroupTo(
          newTopLeftX,
          newTopLeftY,
          elementsInUnit.map((el) => el.original),
          originalElementsMap,
          scene,
        );
      } else {
        const origElement = elementsInUnit[0]?.original;
        const latestElement = elementsInUnit[0]?.latest;
        if (
          origElement &&
          latestElement &&
          isPropertyEditable(latestElement, property)
        ) {
          const [cx, cy] = [
            origElement.x + origElement.width / 2,
            origElement.y + origElement.height / 2,
          ];
          const [topLeftX, topLeftY] = pointRotateRads(
            pointFrom(origElement.x, origElement.y),
            pointFrom(cx, cy),
            origElement.angle,
          );

          let newTopLeftX = property === "x" ? nextValue : topLeftX;
          let newTopLeftY = property === "y" ? nextValue : topLeftY;

          // Convert frame-relative coordinates back to absolute coordinates
          if (origElement.frameId) {
            const containingFrame = originalElementsMap.get(
              origElement.frameId,
            );
            if (containingFrame) {
              if (property === "x") {
                newTopLeftX = nextValue + containingFrame.x;
              } else {
                newTopLeftY = nextValue + containingFrame.y;
              }
            }
          }

          moveElement(
            newTopLeftX,
            newTopLeftY,
            origElement,
            scene,
            originalElementsMap,
            false,
          );
        }
      }
    }

    // Check if any elements are children of auto layout frames and trigger rerendering
    const needsAutoLayoutUpdate = originalElements.some((element) => {
      if (element.frameId) {
        const parentFrame = originalElementsMap.get(element.frameId);
        return parentFrame && isFrameLikeElement(parentFrame) && 
               (parentFrame as any).autoLayout?.enabled;
      }
      return false;
    });

    if (needsAutoLayoutUpdate) {
      // Trigger auto layout rerendering for all frames
      const allElements = scene.getElementsIncludingDeleted();
      const elementsMap = scene.getNonDeletedElementsMap();
      const finalElements = reapplyAutoLayoutToAllFrames(allElements, elementsMap);
      scene.replaceAllElements(finalElements);
    } else {
    scene.triggerUpdate();
    }
    return;
  }

  // Snap to 4px grid when dragging
  const snapTo4px = (value: number) => Math.round(value / 4) * 4;
  const change = snapTo4px(accumulatedChange);

  const changeInTopX = property === "x" ? change : 0;
  const changeInTopY = property === "y" ? change : 0;

  moveElements(
    property,
    changeInTopX,
    changeInTopY,
    originalElements,
    originalElementsMap,
    scene,
  );

  // Check if any elements are children of auto layout frames and trigger rerendering
  const needsAutoLayoutUpdate = originalElements.some((element) => {
    if (element.frameId) {
      const parentFrame = originalElementsMap.get(element.frameId);
      return parentFrame && isFrameLikeElement(parentFrame) && 
             (parentFrame as any).autoLayout?.enabled;
    }
    return false;
  });

  if (needsAutoLayoutUpdate) {
    // Trigger auto layout rerendering for all frames
    const allElements = scene.getElementsIncludingDeleted();
    const elementsMap = scene.getNonDeletedElementsMap();
    const finalElements = reapplyAutoLayoutToAllFrames(allElements, elementsMap);
    scene.replaceAllElements(finalElements);
  } else {
  scene.triggerUpdate();
  }
};

const handleMultiPositionDragFinished: DragFinishedCallbackType = ({
  app,
  originalElements,
}) => {
  // Check if any moved elements are children of auto layout frames
  const elementsMap = app.scene.getNonDeletedElementsMap();
  const needsAutoLayoutUpdate = originalElements?.some((element) => {
    if (element.frameId) {
      const parentFrame = elementsMap.get(element.frameId);
      return parentFrame && isFrameLikeElement(parentFrame) && 
             (parentFrame as any).autoLayout?.enabled;
    }
    return false;
  });

  if (needsAutoLayoutUpdate) {
    // Trigger auto layout rerendering for all frames
    const allElements = app.scene.getElementsIncludingDeleted();
    const newElementsMap = app.scene.getNonDeletedElementsMap();
    const finalElements = reapplyAutoLayoutToAllFrames(allElements, newElementsMap);
    app.scene.replaceAllElements(finalElements);
  }
};

/**
 * Calculates frame-relative coordinates if element is inside a frame,
 * otherwise returns the absolute coordinates
 */
const getDisplayCoordinates = (
  element: ExcalidrawElement,
  elementsMap: ElementsMap,
  topLeftX: number,
  topLeftY: number,
) => {
  // Check if element is inside a frame
  if (element.frameId) {
    const containingFrame = elementsMap.get(element.frameId);
    if (containingFrame) {
      // Return frame-relative coordinates (frame's top-left as 0,0)
      return {
        x: topLeftX - containingFrame.x,
        y: topLeftY - containingFrame.y,
      };
    }
  }

  // Return absolute coordinates if not in frame
  return {
    x: topLeftX,
    y: topLeftY,
  };
};

const MultiPosition = ({
  property,
  elements,
  elementsMap,
  atomicUnits,
  scene,
  appState,
}: MultiPositionProps) => {
  const positions = useMemo(
    () =>
      atomicUnits.map((atomicUnit) => {
        const elementsInUnit = Object.keys(atomicUnit)
          .map((id) => elementsMap.get(id))
          .filter((el) => el !== undefined) as ExcalidrawElement[];

        // we're dealing with a group
        if (elementsInUnit.length > 1) {
          const [x1, y1] = getCommonBounds(elementsInUnit);

          // For groups, check if all elements are in the same frame
          const frameIds = new Set(
            elementsInUnit.map((el) => el.frameId).filter(Boolean),
          );
          if (frameIds.size === 1) {
            const frameId = Array.from(frameIds)[0] as string;
            const containingFrame = elementsMap.get(frameId);
            if (containingFrame) {
              const frameRelativeX = x1 - containingFrame.x;
              const frameRelativeY = y1 - containingFrame.y;
              return (
                Math.round(
                  (property === "x" ? frameRelativeX : frameRelativeY) * 100,
                ) / 100
              );
            }
          }

          return Math.round((property === "x" ? x1 : y1) * 100) / 100;
        }

        const [el] = elementsInUnit;
        const [cx, cy] = [el.x + el.width / 2, el.y + el.height / 2];

        const [topLeftX, topLeftY] = pointRotateRads(
          pointFrom(el.x, el.y),
          pointFrom(cx, cy),
          el.angle,
        );

        // Get frame-relative or absolute coordinates based on element's frame status
        const displayCoords = getDisplayCoordinates(
          el,
          elementsMap,
          topLeftX,
          topLeftY,
        );

        return (
          Math.round(
            (property === "x" ? displayCoords.x : displayCoords.y) * 100,
          ) / 100
        );
      }),
    [atomicUnits, elementsMap, property],
  );

  const value = new Set(positions).size === 1 ? positions[0] : "Mixed";

  return (
    <StatsDragInput
      label={property === "x" ? "X" : "Y"}
      elements={elements}
      dragInputCallback={handlePositionChange}
      value={value}
      property={property}
      scene={scene}
      appState={appState}
      dragFinishedCallback={handleMultiPositionDragFinished}
      tooltip={t(`stats.tooltips.${property}`)}
    />
  );
};

export default MultiPosition;
